import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Card, CardContent, Badge, Button, PlatformMark } from '../../ui';
import { relativeTime } from '../../../utils/dates';
import { ROUTE_PATTERNS, buildRoute } from '../../../constants/Paths';

/**
 * One row per account, not per finding. See attention.js for why.
 *
 * The row is the summary you act on; expanding it shows the individual
 * findings, so the detail is available without six cards competing for the
 * same glance.
 */
export function AttentionQueue({ rows = [] }) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(() => new Set());

	if (!rows.length) return null;

	const toggle = (handle) => {
		setOpen((prev) => {
			const next = new Set(prev);
			if (next.has(handle)) next.delete(handle); else next.add(handle);
			return next;
		});
	};

	return (
		<Card>
			<CardContent className="p-0">
				<ul className="divide-y divide-border">
					{rows.map((row) => {
						const isOpen = open.has(row.handle);
						const age = relativeTime(row.newestAt);
						return (
							<li key={row.handle}>
								<div className="flex flex-wrap items-center gap-3 px-4 py-3">
									<button
										type="button"
										onClick={() => toggle(row.handle)}
										aria-expanded={isOpen}
										className="flex min-w-0 flex-1 items-center gap-3 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
									>
										{isOpen
											? <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
											: <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}

										<div className="relative shrink-0">
											<img src={row.avatar || 'https://placehold.co/32x32'} alt="" className="h-8 w-8 rounded-full bg-muted object-cover" />
											{row.platform && <PlatformMark platform={row.platform} size="xs" className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white" />}
										</div>

										<div className="min-w-0">
											<div className="truncate text-sm font-medium text-foreground">@{row.handle}</div>
											{row.clientName && <div className="truncate text-xs text-muted-foreground">{row.clientName}</div>}
										</div>
									</button>

									<div className="flex flex-wrap items-center gap-2">
										{row.critical > 0 && (
											<Badge className="bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300" size="sm">{row.critical} critical</Badge>
										)}
										{row.high > 0 && (
											<Badge className="bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300" size="sm">{row.high} high</Badge>
										)}
										{age && <span className="text-xs text-muted-foreground">newest {age}</span>}
										{row.scanId && (
											<Button
												size="sm"
												variant="outline"
												onClick={() => navigate(buildRoute(ROUTE_PATTERNS.SCAN_DETAIL, { id: row.scanId }))}
											>
												Open report
											</Button>
										)}
									</div>
								</div>

								{isOpen && (
									<ul className="space-y-2 border-t border-border bg-muted px-4 py-3 pl-11">
										{row.findings.map((f, i) => (
											<li key={`${f.scanId || 'f'}-${i}`} className="flex items-start gap-2 text-sm">
												<span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${f.severity === 'critical' ? 'bg-red-600' : 'bg-orange-500'}`} />
												<span className="min-w-0">
													{f.category && <span className="font-medium text-foreground">{f.category}: </span>}
													<span className="text-muted-foreground">{f.detail}</span>
												</span>
											</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ul>
			</CardContent>
		</Card>
	);
}
